import { useEffect } from 'react'
import { Stack } from '@mui/material'
import { SPACING } from '@common/theme/spacing'
import { useLocation, useParams } from 'react-router-dom'
import { StudyQueryListSchema } from '@common/config/api/client'
import QueriesTableSkeleton from '@features/study_details/Queries/components/QueriesTableSkeleton'
import QueriesTable from '@features/study_details/Queries/components/QueriesTable'
import CreateQueryDrawer from '@features/study_details/Queries/components/CreateQueryDrawer'
import ViewOrEditQueryDrawer from '@features/study_details/Queries/components/ViewOrEditQueryDrawer'
import { useQueriesManagementHandlers } from '@features/study_details/Queries/hooks/useQueriesManagementHandlers'

export default function QCQueries() {
  const { studyId, qcTaskId } = useParams<{
    studyId: string
    qcTaskId: string
  }>()

  const location = useLocation()

  const {
    page,
    queryListParams,
    isCreateDrawerOpen,
    handleCreateQueryClick,
    handleCloseCreateDrawer,
    handleSubmitCreateQuery,
    isViewDrawerOpen,
    selectedQueryDetails,
    isLoadingDetails,
    isLoadingMetadata,
    isErrorDetails,
    handleViewQueryClick,
    handleCloseViewDrawer,
    queriesResponse,
    isLoadingList,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    handleRequestSort,
    handleUpdateStatus,
    handleSendMessage,
    handleUpdateQuery,
    isUpdatingStatus,
    isSendingMessage,
    isUpdatingQuery,
    setQueryListParams,
  } = useQueriesManagementHandlers()

  useEffect(() => {
    if (studyId && qcTaskId) {
      setQueryListParams((prev) => ({
        ...prev,
        qc_task_id: qcTaskId,
        next_cursor: undefined,
        previous_cursor: undefined,
      }))
    }
  }, [studyId, qcTaskId, setQueryListParams])

  useEffect(() => {
    if (location.state?.openCreateQueryDrawer) {
      handleCreateQueryClick()
      window.history.replaceState({}, document.title)
    }
  }, [location.state, handleCreateQueryClick])

  if (!studyId || !qcTaskId) {
    return <div>Loading study information...</div>
  }

  return (
    <>
      <Stack spacing={SPACING.sectionsSpacing}>
        {isLoadingList ? (
          <QueriesTableSkeleton />
        ) : (
          <QueriesTable
            queries={queriesResponse!}
            page={page}
            orderBy={queryListParams.order_by_field as keyof StudyQueryListSchema}
            order={queryListParams.asc_order ? 'asc' : 'desc'}
            searchValue={queryListParams.search ?? ''}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSearch={handleSearch}
            onRequestSort={handleRequestSort}
            onCreateClick={handleCreateQueryClick}
            onViewClick={handleViewQueryClick}
          />
        )}
      </Stack>

      <CreateQueryDrawer
        studyId={studyId}
        open={isCreateDrawerOpen}
        onClose={handleCloseCreateDrawer}
        onSubmit={handleSubmitCreateQuery}
        qcTaskId={qcTaskId}
      />

      {(isViewDrawerOpen || isLoadingDetails || isLoadingMetadata) && (
        <ViewOrEditQueryDrawer
          open={isViewDrawerOpen}
          onClose={handleCloseViewDrawer}
          query={selectedQueryDetails!}
          isLoading={isLoadingDetails}
          isError={isErrorDetails}
          onUpdateStatus={handleUpdateStatus}
          onSendMessage={handleSendMessage}
          onUpdateQuery={handleUpdateQuery}
          isUpdatingStatus={isUpdatingStatus}
          isSendingMessage={isSendingMessage}
          isUpdatingQuery={isUpdatingQuery}
        />
      )}
    </>
  )
}
